import React, { useEffect, useMemo, useState } from 'react';
import useSelectClientSongCollection from './useSelectClientSongCollection';
import SelectBox from '../../../../ui/DataEntry/SelectBox/SelectBox';
import useUrlState from '../../../../Hooks/useUrlState';
import Card from '../../../../ui/Card/Card';
import styles from './ClientSongCollections.module.css';
import useClientSongCollectionStats from './useClientSongCollectionStats';
import MetricCard from '../../../../ui/DataDisplay/Statistics/MetricCard/MetricCard';
import MusicNoteIcon from '../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import VideoCameraIcon from '../../../../SharedComponents/svg/Icons/VideoCameraIcon';
import CalendarMonthIcon from '../../../../SharedComponents/svg/Icons/CalendarMonthIcon';
import useClientSongCollectionSongs, { UseClientSongCollectionSongsParams } from './useClientSongCollectionSongs';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import Select from '../../../../ui/DataEntry/Select/Select';
import { StylesConfig } from 'react-select';
import { GenericDropdownOption } from '../../../../App.types';
import SongsTable from '../SongCollectionTracker/Songs/SongsTable';
import { ClientSongCollection, removeClientSongFromSongCollection, TrndsClientSongTiktokStats } from '@round/api';
import { showNotification } from '../../../../helpers';
import useClientSongCollectionTiktokAudioStats, {
    UseClientSongCollectionTiktokAudioStatsParams,
} from './useClientSongCollectionTiktokAudioStats';
import { AudioPlayerProvider } from '../../../AudioPlayer/AudioPlayerContext';
import TopTiktokAudiosTable from '../../../Song/features/ExploreClientSongs/SongDetails/TopTiktokAudios/TopTiktokAudiosTable';
import ModalRight from '../../../../SharedComponents/ModalRight/ModalRight';
import SongDetails from '../../../Song/features/ExploreClientSongs/SongDetails/SongDetails';
import { ClientSongCollectionsProvider } from '../../../Song/features/AddClientSongToCollection/ClientSongCollectionsContext';
import CloseIcon from '../../../../SharedComponents/svg/Icons/CloseIcon';
import ClientTiktokAudioModal from '../../../TikTok/audios/features/details/ClientTiktokAudioModal/ClientTiktokAudioModal';
import { ClientTiktokAudiosTableRow } from '../../../TikTok/TikTok.types';
import { Button } from '@round/ui-kit';
import CreateClientSongCollectionModal from './CreateClientSongCollection/CreateClientSongCollectionModal';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import Option from '../../../../ui/DataEntry/SelectBox/components/Option/Option';
import { ReactComponent as EditIcon } from '../../../../assets/EditIcon.svg';
import EditClientSongCollectionModal from './EditClientSongCollectionModal/EditClientSongCollectionModal';

type ClientSongCollectionsProps = {
    clientId: number;
};

type UrlState = {
    collection?: number;
    songPage: number;
    songPageSize: number;
    songSearch: string;
    songOrdering: UseClientSongCollectionSongsParams['ordering'];
    songDetails?: number;
    audioPage: number;
    audioPageSize: number;
    audioSearch: string;
    audioOrdering: UseClientSongCollectionTiktokAudioStatsParams['ordering'];
    audioDetails?: number;
};

type SongsOrderingOption = GenericDropdownOption<UseClientSongCollectionSongsParams['ordering']>;
const songsOrderingOptions: SongsOrderingOption[] = [
    { label: 'Total Videos', value: '-total_video_count' },
    { label: 'Daily Growth (# videos)', value: '-total_video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-total_video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-total_video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-total_video_count_weekly_change_relative' },
];

type AudiosOrderingOption = GenericDropdownOption<UseClientSongCollectionTiktokAudioStatsParams['ordering']>;
const audiosOrderingOptions: AudiosOrderingOption[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const ClientSongCollections = ({ clientId }: ClientSongCollectionsProps) => {
    const [urlState, setUrlState] = useUrlState<UrlState>({
        songPage: 1,
        songPageSize: 10,
        songSearch: '',
        songOrdering: '-total_video_count_daily_change',
        audioPage: 1,
        audioPageSize: 10,
        audioSearch: '',
        audioOrdering: '-video_count_daily_change',
    });
    const collectionId = urlState.collection ? Number(urlState.collection) : undefined;
    const [isCreateSongCollectionModalOpen, setIsCreateSongCollectionModalOpen] = useState(false);
    const [isEditSongCollectionModalOpen, setIsEditSongCollectionModalOpen] = useState(false);
    const [songCollectionToEdit, setSongCollectionToEdit] = useState<ClientSongCollection | null>(null);

    const {
        collections,
        selectedCollection,
        isLoading,
        onSelectedCollectionChange,
        count,
        initialCollectionLoading,
        search,
        onSearchChange,
        loadMore,
        addCollection,
        updateCollection,
        isInitialized: isCollectionOptionListInitialized,
        hasErrorLoading: hasErrorLoadingCollectionOptionList,
    } = useSelectClientSongCollection(clientId, collectionId);

    useEffect(() => {
        if (!collectionId && collections.length) {
            setUrlState({ collection: collections[0].id });
        }
    }, [collectionId, collections, setUrlState]);

    const {
        stats: songCollectionStats,
        isLoading: isSongCollectionStatsLoading,
        reset: resetCollectionStats,
    } = useClientSongCollectionStats(clientId, collectionId);

    const [isSongDetailsModalOpen, setIsSongDetailsModalOpen] = useState(Boolean(urlState.songDetails));
    const [selectedSongRow, setSelectedSongRow] = useState<TrndsClientSongTiktokStats | null>(null);
    const [songsDisplayed, setSongsDisplayed] = useState(0);
    const {
        songs,
        count: songsCount,
        reset: resetSongs,
        isLoading: songsLoading,
        hasErrorLoadingStats: hasErrorLoadingSongs,
        refresh: refreshSongs,
    } = useClientSongCollectionSongs({
        clientId,
        songCollectionId: collectionId,
        search: urlState.songSearch,
        page: Number(urlState.songPage),
        page_size: Number(urlState.songPageSize),
        ordering: urlState.songOrdering as UseClientSongCollectionSongsParams['ordering'],
    });

    const selectCollection = (collection: SelectBoxOptionWithId) => {
        onSelectedCollectionChange(collection);
        setUrlState({ collection: collection.id });
        resetCollectionStats();
        resetSongs();
        resetAudios();
    };

    const [isAudioModalOpen, setIsAudioModalOpen] = useState(Boolean(urlState.audioDetails));
    const [audiosDisplayed, setAudiosDisplayed] = useState(0);
    const {
        reset: resetAudios,
        refresh: refreshAudios,
        count: audiosCount,
        isLoading: audiosLoading,
        stats: audios,
        assets: audioAssets,
    } = useClientSongCollectionTiktokAudioStats({
        clientId,
        songCollectionId: collectionId,
        page: Number(urlState.audioPage),
        page_size: Number(urlState.audioPageSize),
        search: urlState.audioSearch,
        ordering: urlState.audioOrdering as UseClientSongCollectionTiktokAudioStatsParams['ordering'],
    });

    const removeSongFromCollection = async (stats: TrndsClientSongTiktokStats) => {
        if (!collectionId) {
            return;
        }

        try {
            const response = await removeClientSongFromSongCollection({
                songCollectionId: collectionId,
                clientId,
                songId: stats.song.id,
            });

            if (response.status === 204) {
                await Promise.allSettled([refreshSongs(), refreshAudios()]);
                showNotification('Song removed', 'info');
                return;
            }

            if (response.status === 400) {
                showNotification(response.data[0] || "Couldn't remove the song", 'error');
                return;
            }

            if (response.status === 403) {
                showNotification(response.data.detail, 'error');
                return;
            }
        } catch {
            showNotification("Couldn't remove the song", 'error');
        }
    };

    const closeSongDetailsModal = () => {
        setIsSongDetailsModalOpen(false);
        setSelectedSongRow(null);
        setUrlState({ songDetails: undefined });
    };

    const selectedAudioStats = useMemo<ClientTiktokAudiosTableRow | null>(() => {
        const audio = audios.find((audio) => audio.audio_id === Number(urlState.audioDetails));
        if (!audio) {
            return null;
        }

        const asset = audioAssets.find((asset) => asset.audio_id === audio.audio_id);
        return {
            ...audio,
            audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
            image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
            dailyGrowth: audio.standardised_data.daily_video_counts.map((item) => ({
                value: item.change,
                timestamp: item.timestamp,
            })),
        };
    }, [audioAssets, audios, urlState.audioDetails]);

    return (
        <Card className={styles.container}>
            <div className={styles.header}>
                <div className={styles.collectionSelectContainer}>
                    <SelectBox
                        loading={initialCollectionLoading}
                        value={selectedCollection}
                        onChange={selectCollection}
                        options={isCollectionOptionListInitialized ? collections : new Array(5)}
                        optionsCount={isCollectionOptionListInitialized ? count : 5}
                        optionsLoading={
                            isLoading || (!isCollectionOptionListInitialized && !hasErrorLoadingCollectionOptionList)
                        }
                        searchable
                        search={search}
                        setSearch={onSearchChange}
                        loadMore={loadMore}
                        renderDropdownBottom={(setShowDropdown) => (
                            <Button
                                className={styles.createCollectionButton}
                                onClick={() => {
                                    setShowDropdown(false);
                                    setIsCreateSongCollectionModalOpen(true);
                                }}
                            >
                                Create
                            </Button>
                        )}
                        renderItem={(props) => (
                            <Option
                                {...props}
                                renderRight={() => (
                                    <Button
                                        className={styles.editCollectionButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.setShowDropdown(false);
                                            const collection = collections.find((c) => c.id === props.item?.id);
                                            if (!collection) {
                                                return;
                                            }

                                            setSongCollectionToEdit({
                                                id: collection.id,
                                                name: collection.name,
                                                picture: collection.picture,
                                            });
                                            setIsEditSongCollectionModalOpen(true);
                                        }}
                                    >
                                        <EditIcon />
                                    </Button>
                                )}
                            />
                        )}
                    />

                    <CreateClientSongCollectionModal
                        clientId={clientId}
                        isOpen={isCreateSongCollectionModalOpen}
                        onClose={() => setIsCreateSongCollectionModalOpen(false)}
                        onCreated={(collection) => {
                            setIsCreateSongCollectionModalOpen(false);
                            addCollection(collection);
                            selectCollection({
                                id: collection.id,
                                name: collection.name,
                                picture: collection.picture ?? '',
                            });
                        }}
                    />

                    <EditClientSongCollectionModal
                        isOpen={isEditSongCollectionModalOpen}
                        onClose={() => {
                            setIsEditSongCollectionModalOpen(false);
                            setSongCollectionToEdit(null);
                        }}
                        onUpdated={(collection) => {
                            setIsEditSongCollectionModalOpen(false);
                            setSongCollectionToEdit(null);
                            updateCollection(collection);
                        }}
                        collection={songCollectionToEdit}
                        clientId={clientId}
                    />
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.statsContainer}>
                    <MetricCard
                        baseColor="#0090FF"
                        loading={isSongCollectionStatsLoading}
                        value={songCollectionStats?.total_audios_tracked}
                        label="Total Audios Tracked"
                        Icon={MusicNoteIcon}
                    />
                    <MetricCard
                        baseColor="#F7685B"
                        loading={isSongCollectionStatsLoading}
                        value={songCollectionStats?.total_video_count}
                        label="Total Video Creations"
                        Icon={VideoCameraIcon}
                    />
                    <MetricCard
                        baseColor="#FFA114"
                        loading={isSongCollectionStatsLoading}
                        value={songCollectionStats?.new_video_count_seven_days}
                        label="creations in the last 7 days"
                        Icon={CalendarMonthIcon}
                    />
                    <MetricCard
                        baseColor="#48D98A"
                        loading={isSongCollectionStatsLoading}
                        value={songCollectionStats?.new_video_count_one_day}
                        label="Daily Creations"
                        Icon={CalendarMonthIcon}
                    />
                </div>

                <div>
                    <h2 className={styles.title}>Songs</h2>
                    <p className={styles.subtitle}>
                        {songsLoading ? (
                            <Skeleton width="10rem" />
                        ) : (
                            `displaying ${songsDisplayed} out of ${songsCount} song${songsCount !== 1 ? 's' : ''}`
                        )}
                    </p>
                </div>

                <div className={styles.toolbar}>
                    <SearchInput
                        className={styles.search}
                        value={urlState.songSearch}
                        onChange={(search) => {
                            setUrlState({ songPage: 1, songSearch: search });
                            resetSongs();
                        }}
                    />

                    <div className={styles.toolbarItem}>
                        <label className={styles.toolbarItemLabel} htmlFor="songsOrdering">
                            Sort By
                        </label>
                        <Select
                            className={styles.orderingSelect}
                            styles={sortingSelectStyles}
                            id="songsOrdering"
                            options={songsOrderingOptions}
                            value={songsOrderingOptions.find((o) => o.value === urlState.songOrdering)}
                            onChange={(o) => setUrlState({ songOrdering: o?.value ?? '-total_video_count' })}
                        />
                    </div>
                </div>

                <SongsTable
                    noDataLabel={hasErrorLoadingSongs ? 'Something went wrong' : 'No data found'}
                    loading={songsLoading}
                    data={songs}
                    count={songsCount}
                    ordering={urlState.songOrdering as UseClientSongCollectionSongsParams['ordering']}
                    page={Number(urlState.songPage)}
                    setPage={(page) => setUrlState({ songPage: page })}
                    pageSize={Number(urlState.songPageSize)}
                    setPageSize={(pageSize) => setUrlState({ songPageSize: pageSize ?? 10 })}
                    onRemoveSongClicked={removeSongFromCollection}
                    onPaginationChange={(state) => setSongsDisplayed(state.pageRows?.length)}
                    onRowClick={(row) => {
                        setSelectedSongRow(row);
                        setUrlState({ songDetails: row.song.id });
                        setIsSongDetailsModalOpen(true);
                    }}
                />

                <ModalRight
                    className={styles.modal}
                    isModalOpen={isSongDetailsModalOpen}
                    closeModal={closeSongDetailsModal}
                >
                    <ClientSongCollectionsProvider>
                        <div className={styles.modalContent}>
                            <button className={styles.modalCloseButton} onClick={closeSongDetailsModal}>
                                <CloseIcon width={16} height={16} />
                            </button>
                            <SongDetails
                                key={Number(urlState.songDetails)}
                                songId={Number(urlState.songDetails)}
                                clientId={clientId}
                                songStats={selectedSongRow}
                            />
                        </div>
                    </ClientSongCollectionsProvider>
                </ModalRight>

                <div>
                    <h2 className={styles.title}>Top Audios</h2>
                    <p className={styles.subtitle}>
                        {songsLoading ? (
                            <Skeleton width="10rem" />
                        ) : (
                            `displaying ${audiosDisplayed} out of ${audiosCount} audio${audiosCount !== 1 ? 's' : ''}`
                        )}
                    </p>
                </div>

                <div className={styles.toolbar}>
                    <SearchInput
                        className={styles.search}
                        value={urlState.audioSearch}
                        onChange={(search) => {
                            setUrlState({ audioPage: 1, audioSearch: search });
                        }}
                    />

                    <div className={styles.toolbarItem}>
                        <label className={styles.toolbarItemLabel} htmlFor="audiosOrdering">
                            Sort By
                        </label>
                        <Select
                            className={styles.orderingSelect}
                            styles={sortingSelectStyles}
                            id="audiosOrdering"
                            options={audiosOrderingOptions}
                            value={audiosOrderingOptions.find((o) => o.value === urlState.audioOrdering)}
                            onChange={(o) => setUrlState({ audioOrdering: o?.value ?? '-video_count_daily_change' })}
                        />
                    </div>
                </div>

                <AudioPlayerProvider>
                    <TopTiktokAudiosTable
                        order={urlState.audioOrdering as UrlState['audioOrdering']}
                        loading={audiosLoading}
                        data={audios.map((audio) => {
                            const asset = audioAssets.find((asset) => audio.audio_id === asset.audio_id);
                            return {
                                ...audio,
                                audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
                                image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
                                dailyGrowth: audio.standardised_data.daily_video_counts.map((item) => ({
                                    value: item.change,
                                    timestamp: item.timestamp,
                                })),
                            };
                        })}
                        count={audiosCount}
                        page={Number(urlState.audioPage)}
                        setPage={(page) => setUrlState({ audioPage: page })}
                        pageSize={Number(urlState.audioPageSize)}
                        setPageSize={(pageSize) => setUrlState({ audioPageSize: pageSize ?? 10 })}
                        onPaginationChange={(state) => setAudiosDisplayed(state.pageRows.length)}
                        onRowClick={(row) => {
                            setUrlState({ audioDetails: row.audio_id });
                            setIsAudioModalOpen(true);
                        }}
                    />

                    <ClientTiktokAudioModal
                        isModalOpen={isAudioModalOpen}
                        closeModal={() => {
                            setUrlState({ audioDetails: undefined });
                            setIsAudioModalOpen(false);
                        }}
                        audioId={Number(urlState.audioDetails)}
                        clientId={clientId}
                        audioStatsTableRow={selectedAudioStats}
                    />
                </AudioPlayerProvider>
            </div>
        </Card>
    );
};

export default ClientSongCollections;
